/**
 * VaultSummaryWalkthrough — one-time, dismissible callout that appears the
 * moment a user adds their first Trusted Contact. Explains, in plain English:
 *   1) what's actually inside the Continuity Records,
 *   2) who receives it (and when),
 *   3) why it exists.
 *
 * Persists dismissal in localStorage per vault_id so it never shows twice for
 * the same user, but can still show fresh for a different account on the same
 * device.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { CheckCircle2, ShieldCheck, Users, FileLock2, X } from 'lucide-react';

const STORAGE_KEY = 'ev_summary_walkthrough_dismissed';

function keyFor(vaultId) {
  return `${STORAGE_KEY}:${vaultId || 'default'}`;
}

export function VaultSummaryWalkthrough({ vaultId, successorCount }) {
  const storageKey = keyFor(vaultId);
  const initiallyDismissed = typeof window !== 'undefined'
    && window.localStorage.getItem(storageKey) === '1';
  const [dismissed, setDismissed] = useState(initiallyDismissed);

  // Show only when the user has JUST added their first Trusted Contact,
  // and has not already seen (and dismissed) the walkthrough for this vault.
  if (dismissed || (successorCount || 0) !== 1) return null;

  const handleDismiss = () => {
    try { window.localStorage.setItem(storageKey, '1'); } catch (_) { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className="border border-emerald-200 rounded-lg p-4 bg-emerald-50/70 shadow-xs"
      data-testid="vault-summary-walkthrough"
    >
      <div className="flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
        <div className="flex-1">
          <h4 className="font-semibold text-emerald-900 text-sm sm:text-base">
            Nicely done. Here&rsquo;s what happens next.
          </h4>
          <p className="text-sm text-slate-700 mt-1.5 leading-relaxed">
            Now that you&rsquo;ve added your first Trusted Contact, your <strong>Continuity Records</strong> quietly come to life. A quick tour of what it is and who touches it:
          </p>
          <ul className="mt-3 space-y-2 text-sm text-slate-700">
            <li className="flex items-start gap-2">
              <FileLock2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
              <span><strong>What&rsquo;s in it.</strong> An encrypted PDF listing your Trusted Contacts and the assets you&rsquo;ve catalogued in your Continuity System &mdash; the &ldquo;where things live&rdquo; map, not your account balances or passwords.</span>
            </li>
            <li className="flex items-start gap-2">
              <Users className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
              <span><strong>Who sees it.</strong> Only you can view it while you&rsquo;re here. Trusted Contacts only ever receive it if the Continuity Trigger completes AND two MyInvestFolio administrators independently approve the release.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
              <span><strong>Why it exists.</strong> So the people you trust can <em>find</em> your investments if you&rsquo;re ever unable to explain them yourself. It is not a will, trust, or probate instruction.</span>
            </li>
          </ul>
          <div className="mt-4 flex justify-end">
            <Button
              size="sm"
              onClick={handleDismiss}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="vault-walkthrough-dismiss"
            >
              Got it
            </Button>
          </div>
        </div>
        <Button
          variant="ghost" size="sm" onClick={handleDismiss}
          className="text-slate-400 hover:text-slate-700 shrink-0 -mr-1 -mt-1"
          aria-label="Dismiss walkthrough"
          data-testid="vault-walkthrough-close"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
